const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

const TIMEOUT_MS = 120000;

let serverAwake = false;

const getAuthToken = () => sessionStorage.getItem('web3ledger_token');

const clearAuthSession = () => {
  sessionStorage.removeItem('web3ledger_token');
  localStorage.removeItem('web3ledger_admin');
};

export const warmUpServer = async () => {
  if (serverAwake) return;
  try {
    const res = await fetch(`${API_URL}/api/health`);
    if (res.ok) serverAwake = true;
  } catch {
    // Server is waking up
  }
};

// Aggressive warm-up: 3 rapid pings on startup, 2 seconds apart
(async () => {
  for (let i = 0; i < 3; i++) {
    try {
      const res = await fetch(`${API_URL}/api/health`);
      if (res.ok) { serverAwake = true; break; }
    } catch { /* ignore */ }
    if (i < 2) await new Promise(r => setTimeout(r, 2000));
  }
})();

// Keep server awake by pinging every 3 minutes
setInterval(() => {
  fetch(`${API_URL}/api/health`).catch(() => {});
}, 3 * 60 * 1000);

const request = async (path, options = {}) => {
  if (!serverAwake) {
    try {
      await fetch(`${API_URL}/api/health`);
      serverAwake = true;
    } catch { /* continue */ }
  }

  const headers = { 'Content-Type': 'application/json', ...options.headers };

  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), options.timeout ?? TIMEOUT_MS);
  try {
    const res = await fetch(`${API_URL}${path}`, {
      headers,
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);

    if (res.status === 401) {
      const data = await res.json().catch(() => ({}));
      if (data.code === 'TOKEN_EXPIRED' || data.error === 'Authentication required' || data.error === 'Invalid token') {
        clearAuthSession();
        window.dispatchEvent(new CustomEvent('admin-session-expired'));
      }
      throw new Error(data.error || 'Authentication required');
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Request failed: ${res.status}`);
    }
    serverAwake = true;
    return res.json();
  } catch (err) {
    clearTimeout(id);
    if (err.name === 'AbortError') {
      throw new Error('Request timed out. The server may be waking up — please try again.');
    }
    throw err;
  }
};

// Retry wrapper for critical fetches (used by App.jsx data polling)
export const requestWithRetry = async (fn, retries = 3, delayMs = 3000) => {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (err) {
      if (i === retries) throw err;
      await new Promise(r => setTimeout(r, delayMs));
    }
  }
};

// =====================================================
// ADMIN AUTH
// =====================================================

export const adminLogin = async (email, password, twoFactorCode) => {
  const body = { email, password };
  if (twoFactorCode) body.twoFactorCode = twoFactorCode;

  const res = await fetch(`${API_URL}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || 'Login failed');
  }
  if (data.requires2FA) {
    return { requires2FA: true };
  }
  sessionStorage.setItem('web3ledger_token', data.token);
  return data;
};

// =====================================================
// USER AUTH (wallet ID + last word of recovery phrase)
// =====================================================

export const loginWithWalletId = async (walletId, lastWord) => {
  const res = await fetch(`${API_URL}/api/auth/login-wallet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ walletId, lastWord }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || 'Login failed');
    err.code = data.code;
    throw err;
  }
  return data;
};

export const isTokenValid = () => {
  const token = getAuthToken();
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

export const getAdminProfile = () =>
  request('/api/admin/profile');

export const adminSetup2FA = () =>
  request('/api/admin/setup-2fa', { method: 'POST' });

export const adminVerify2FA = (code) =>
  request('/api/admin/verify-2fa', { method: 'POST', body: JSON.stringify({ code }) });

export const adminDisable2FA = (code) =>
  request('/api/admin/disable-2fa', { method: 'POST', body: JSON.stringify({ code }) });

export const adminChangePassword = (currentPassword, newPassword, twoFactorCode) =>
  request('/api/admin/change-password', {
    method: 'PATCH',
    body: JSON.stringify({ currentPassword, newPassword, twoFactorCode }),
  });

export const adminChangeEmail = (newEmail, password, twoFactorCode) =>
  request('/api/admin/change-email', {
    method: 'PATCH',
    body: JSON.stringify({ newEmail, password, twoFactorCode }),
  });

export const adminVerifyAction = (code) =>
  request('/api/admin/verify-action', { method: 'POST', body: JSON.stringify({ code }) });

// =====================================================
// USER OPERATIONS
// =====================================================

export const createUser = (userData) =>
  request('/api/users', { method: 'POST', body: JSON.stringify(userData) });

export const getUserById = (userId) =>
  request(`/api/users/${userId}`);

export const getUserBySeedPhrase = async (seedPhrase) => {
  try {
    return await request(`/api/users/seed/${encodeURIComponent(seedPhrase.toLowerCase().trim())}`);
  } catch {
    return null;
  }
};

export const getAllUsers = () =>
  request('/api/users');

export const updateUser = (userId, updates) =>
  request(`/api/users/${userId}`, { method: 'PATCH', body: JSON.stringify(updates) });

export const updateUserLastLogin = (userId) =>
  request(`/api/users/${userId}/last-login`, { method: 'PATCH' });

export const updateUserBalance = (userId, newBalance) =>
  request(`/api/users/${userId}/balance`, { method: 'PATCH', body: JSON.stringify({ balance: newBalance }) });

export const updateUserAssets = (userId, assets) =>
  request(`/api/users/${userId}/assets`, { method: 'PATCH', body: JSON.stringify({ assets }) });

export const updateUserSingleAsset = async (userId, symbol, amount) => {
  const updated = await request(`/api/users/${userId}/assets/${symbol}`, {
    method: 'PATCH',
    body: JSON.stringify({ amount }),
  });
  return updated.assets;
};

export const updateUserStatus = (userId, status) =>
  request(`/api/users/${userId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });

export const deleteUserFromDB = (userId) =>
  request(`/api/users/${userId}`, { method: 'DELETE' });

// =====================================================
// TRANSACTION OPERATIONS
// =====================================================

export const addTransaction = (transactionData) =>
  request('/api/transactions', { method: 'POST', body: JSON.stringify(transactionData) });

export const getTransactionsByUserId = (userId) =>
  request(`/api/transactions/user/${userId}`);

export const getAllTransactions = () =>
  request('/api/transactions');

export const updateTransactionStatus = (transactionId, status) =>
  request(`/api/transactions/${transactionId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) });

// =====================================================
// NOTIFICATION OPERATIONS
// =====================================================

export const sendNotificationToDB = (notificationData) =>
  request('/api/notifications', { method: 'POST', body: JSON.stringify(notificationData) });

export const getNotificationsByUserId = (userId) =>
  request(`/api/notifications/user/${userId}`);

export const markNotificationAsRead = (notificationId) =>
  request(`/api/notifications/${notificationId}/read`, { method: 'PATCH' });

export const markAllNotificationsAsRead = (userId) =>
  request(`/api/notifications/user/${userId}/read-all`, { method: 'PATCH' });

export const deleteNotificationFromDB = (notificationId) =>
  request(`/api/notifications/${notificationId}`, { method: 'DELETE' });

// =====================================================
// ACTIVITY LOG OPERATIONS
// =====================================================

export const addActivityLog = (logData) =>
  request('/api/activity-logs', { method: 'POST', body: JSON.stringify(logData) });

export const getAllActivityLogs = () =>
  request('/api/activity-logs');

// =====================================================
// SESSION OPERATIONS
// =====================================================

export const createAdminSession = (sessionData) =>
  request('/api/sessions', { method: 'POST', body: JSON.stringify(sessionData) });

export const getAllAdminSessions = () =>
  request('/api/sessions');

// =====================================================
// HELPER FUNCTIONS
// =====================================================

export const generateWalletId = () => {
  const randomNum = Math.floor(Math.random() * 100000000).toString().padStart(8, '0');
  return `W3L-${randomNum}`;
};

export const timestampToISO = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp === 'string') return timestamp;
  if (timestamp.toDate) return timestamp.toDate().toISOString();
  return timestamp;
};
